import {
  RefObject,
  HTMLAttributes,
  forwardRef,
  PropsWithChildren,
  ReactNode,
} from 'react';
import styled from 'styled-components';
import { colors, s8 } from '@ardoq/design-tokens';
import { caption } from '@ardoq/typography';
import { Label, LABEL_SELECTOR } from '../Label/Label';
import { BUTTON_SELECTOR } from '@ardoq/button';
import { basicLinkCSS } from '@ardoq/style-helpers';
import { FormSize } from '../../types';
import { CloseIcon } from '@ardoq/icons';
import {
  InputErrorMessage,
  InputHintMessage,
  InputWarningMessage,
} from './atoms';
import { WithPopoverPlacement, WithPopover } from '@ardoq/popovers';
import { FlexBox } from '@ardoq/layout';
import { getChildElementId } from '../../utils';
import { CommonFieldProps } from './types';

export type FieldProps = Omit<
  CommonFieldProps,
  'hasError' | 'hasWarning' | 'label'
> &
  PropsWithChildren<{
    ref?: RefObject<HTMLDivElement>;
  }> &
  Pick<
    HTMLAttributes<HTMLDivElement>,
    'tabIndex' | 'onBlur' | 'onKeyDown' | 'onPaste' | 'className'
  > & {
    label?: ReactNode;
  };

export const FieldWrapper = styled.div<{
  $formSize?: FormSize;
  $alignItemsCenter?: boolean;
}>`
  width: ${({ $formSize = FormSize.AUTO }) => $formSize};
  ${({ $alignItemsCenter }) =>
    $alignItemsCenter && 'display: flex; align-items: center;'}

  ${LABEL_SELECTOR} + ${BUTTON_SELECTOR} {
    margin-top: 0;
  }

  & > ${BUTTON_SELECTOR} {
    margin-top: ${s8};
  }
`;

export const HelperTextWrapper = styled.div<{ $isDisabled?: boolean }>`
  ${caption};
  color: ${({ $isDisabled }) =>
    $isDisabled ? colors.textDisabled : colors.textSubtle};
  margin-bottom: ${s8};
  overflow-wrap: anywhere;

  & > a {
    ${basicLinkCSS}
  }
`;

export const FIELD_SELECTOR = FieldWrapper.toString();

const CloseFieldIcon = styled(CloseIcon)`
  cursor: pointer;
  color: ${colors.iconAction};
  &:hover {
    color: ${colors.iconActionHover};
  }
`;

const ChildrenWrapper = styled.div`
  & > ${BUTTON_SELECTOR} {
    margin-top: ${s8};
  }
`;

export const Field = forwardRef<HTMLDivElement, FieldProps>(
  (
    {
      children,
      label,
      labelId,
      inputId,
      isDisabled,
      indicator,
      helperText,
      errorMessage,
      hintMessage,
      warningMessage,
      onCloseFieldClick,
      closeFieldIconTooltip,
      __DS_ONLY_fieldStyle,
      dataTestId,
      popoverContent,
      popoverHelpContent,
      formSize,
      i18nLocale,
      ...rest
    },
    ref
  ) => (
    <FieldWrapper
      ref={ref}
      style={__DS_ONLY_fieldStyle}
      data-test-id={dataTestId}
      data-testid={dataTestId}
      $formSize={formSize}
      {...rest}
    >
      {label && (
        <FlexBox paddingBottom="small" justify="space-between">
          <Label
            id={labelId}
            inputId={inputId}
            isDisabled={isDisabled}
            indicator={indicator}
            popoverHelpContent={popoverHelpContent}
            i18nLocale={i18nLocale}
          >
            {label}
          </Label>
          {onCloseFieldClick && (
            <FlexBox marginLeft="xsmall">
              <CloseFieldIcon
                deprecated_onClick={onCloseFieldClick}
                data-tooltip-text={closeFieldIconTooltip}
              />
            </FlexBox>
          )}
        </FlexBox>
      )}
      {helperText && (
        <HelperTextWrapper
          $isDisabled={isDisabled}
          id={getChildElementId(inputId, 'help-text')}
        >
          {helperText}
        </HelperTextWrapper>
      )}
      <WithPopover
        content={popoverContent}
        preferredPlacement={WithPopoverPlacement.TOP}
      >
        {/* WithPopover needs an element to append the popover-id attribute on */}
        <ChildrenWrapper>{children}</ChildrenWrapper>
      </WithPopover>
      {hintMessage && (
        <InputHintMessage id={getChildElementId(inputId, 'hint')}>
          {hintMessage}
        </InputHintMessage>
      )}
      {errorMessage && (
        <InputErrorMessage id={getChildElementId(inputId, 'error')}>
          {errorMessage}
        </InputErrorMessage>
      )}
      {!errorMessage && warningMessage && (
        <InputWarningMessage id={getChildElementId(inputId, 'warning')}>
          {warningMessage}
        </InputWarningMessage>
      )}
    </FieldWrapper>
  )
);
